"use client";

import { useState } from "react";

interface PrepViewProps {
  candidateName: string;
  position: string;
  isDesktop: boolean;
  onStart: () => Promise<void>;
  loading?: boolean;
  /**
   * Hiring company shown in the prep copy + header. Defaults to "Kolleno" so the
   * legacy white-label /kolleno/* pages render unchanged; the general CRM
   * candidate flow passes the actual vacancy's company name.
   */
  companyName?: string;
}

export function PrepView({ candidateName, position, isDesktop, onStart, loading: externalLoading, companyName = "Kolleno" }: PrepViewProps) {
  const brand = companyName.trim() || "Kolleno";
  const brandInitial = brand.charAt(0).toUpperCase();
  const [loading, setLoading] = useState(false);
  const isDisabled = loading || externalLoading;
  const [error, setError] = useState("");

  async function handleStart() {
    // Guard against double-click / React strict-mode double-invoke: if we're
    // already in flight (internal `loading` or the parent signals `externalLoading`),
    // bail out. This prevents a second POST /api/crm/start-interview which would
    // orphan an in_progress candidate row.
    if (loading || externalLoading) return;
    setLoading(true);
    setError("");
    try {
      await onStart();
    } catch (e: any) {
      setError(e.message || "Something went wrong");
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-hf-paper text-hf-ink">
      <div className="border-b border-hf-line px-6 py-4 flex items-center gap-3">
        <div className="w-8 h-8 rounded bg-gradient-to-br from-hf-primary to-hf-primary-strong flex items-center justify-center font-bold text-sm text-white">{brandInitial}</div>
        <span className="font-semibold text-hf-ink">{brand} — Interview Preparation</span>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-12 space-y-10">
        <div>
          <p className="text-hf-primary text-sm mb-2">Ready to begin, {candidateName.split(" ")[0]}</p>
          <h1 className="text-3xl font-bold mb-3">Before we start</h1>
          <p className="text-hf-muted">Here's what to expect during your interview for <span className="text-hf-ink">{position}</span>.</p>
        </div>

        {/* Steps */}
        <div className="space-y-4">
          {[
            { icon: "🎙️", title: "Voice interview", desc: "Our AI interviewer will ask you questions and listen to your answers. Speak naturally — there's no time pressure per question." },
            { icon: "💬", title: "~30–45 minutes", desc: "The interview covers 7 competency areas. You can take a moment to think before answering each question." },
            { icon: "📊", title: "Instant report", desc: `After the interview, ${brand}'s hiring team receives a detailed report with your scores and feedback.` },
          ].map((s) => (
            <div key={s.title} className="flex gap-4 p-4 rounded-xl bg-hf-card border border-hf-line shadow-[0_10px_30px_-15px_rgba(20,19,15,0.25)]">
              <span className="text-2xl mt-0.5">{s.icon}</span>
              <div>
                <p className="font-medium text-hf-ink">{s.title}</p>
                <p className="text-hf-muted text-sm mt-0.5">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Recording notice — only on desktop */}
        {isDesktop && (
          <div className="rounded-xl border border-hf-primary/30 bg-hf-primary-soft p-5 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-lg">🔴</span>
              <p className="font-semibold text-hf-primary-strong">This interview will be recorded</p>
            </div>
            <p className="text-hf-muted text-sm leading-relaxed">
              To help the hiring team review your interview, we record your screen during the session.
              When you click "Start Interview", your browser will ask you to choose what to share — you can select this tab, a window, or your entire screen.
            </p>
            <p className="text-hf-faint text-xs mt-1">
              The recording is only shared with {brand}'s hiring team and is not used for any other purpose.
            </p>
          </div>
        )}

        {/* Tips */}
        <div className="rounded-xl bg-hf-surface border border-hf-line p-5">
          <p className="text-sm font-medium text-hf-ink mb-3">Quick tips for a great interview</p>
          <ul className="space-y-2 text-sm text-hf-muted">
            <li className="flex gap-2"><span className="text-hf-success">✓</span> Find a quiet place with a stable internet connection</li>
            <li className="flex gap-2"><span className="text-hf-success">✓</span> Use headphones if possible to avoid echo</li>
            <li className="flex gap-2"><span className="text-hf-success">✓</span> Keep this tab open and in focus throughout the interview</li>
            <li className="flex gap-2"><span className="text-hf-success">✓</span> Speak clearly and take your time — quality over speed</li>
          </ul>
        </div>

        {error && (
          <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-red-600 text-sm">
            {error === "Please share a browser tab, not a window or desktop."
              ? "⚠️ Please select \"This Tab\" in the screen sharing dialog, not your entire screen or a window."
              : `⚠️ ${error}`}
          </div>
        )}

        <button
          onClick={handleStart}
          disabled={isDisabled}
          className="w-full bg-hf-primary hover:bg-hf-primary-strong disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-4 rounded-btn transition-colors flex items-center justify-center gap-2 text-lg shadow-[0_10px_26px_-10px_rgba(47,107,246,0.6)]"
        >
          {loading
            ? <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />Setting up your interview...</>
            : externalLoading
            ? <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />Preparing interview...</>
            : "Start Interview →"}
        </button>

        <p className="text-center text-hf-faint text-xs">
          By starting the interview you agree to the session being recorded and reviewed by {brand}'s hiring team.
        </p>
      </div>
    </div>
  );
}
